import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { Button, ConfigProvider, Form, Modal, Row, Tooltip } from 'antd';

import {
  clearEditorData,
  clearImageEditorImage,
  setAddFeedcardModalOpened,
  setCardProps,
  setEditingCardProps,
  setFeedcardValidationFailed,
  setIsInitialScrapingDone,
} from '../actions';
import { CardType, FeedCardImageType } from '../graphql/api';
import { FeedCard, IState } from '../graphql/types';
import useFeedCardForm from '../hooks/useFeedCardForm';
import { CardsShowOff, ICard } from './CardForms/CardsShowOff';

import FeedCardIcon from './CardForms/FeedCardIcon';
import CardCreateForm from './CardForms/CardCreateForm';

import styles from './Components.module.css';
import { FeedcardType } from './utils';
import AvatarEditor from 'react-avatar-editor';
import { feedCardModalTheme } from '../theme';
import { nextTick } from 'process';

const fcModalCollection: HTMLCollectionOf<HTMLElement> =
  document.getElementsByClassName(
    'new-feedcard-modal'
  ) as HTMLCollectionOf<HTMLElement>;

const AddFeedCardModal: React.FC = () => {
  const dispatch = useDispatch();
  const cardProps = useSelector<IState, Partial<FeedCard>>(
    state => state.cardProps || {}
  );

  const [isDragging, setIsDragging] = useState(false);

  const { recording, time, selectedWord, isInLibrary } = cardProps;
  const type = useSelector<IState, CardType | undefined>(
    state => state.cardProps?.type ?? undefined
  );
  const addFeedcardModalOpened = useSelector<IState, boolean>(
    state => !!state.addFeedcardModalOpened
  );
  const graphicFormScrollPos = useSelector<IState, null | number>(
    state => state.graphicFormScrollPos
  );

  const [selectedType, setSelectedType] = useState<CardType | undefined>();

  useEffect(() => {
    setSelectedType(type);
  }, [type]);

  const [editorRef, setEditorRef] = useState<AvatarEditor | null>(null);

  const handlePageResize = useCallback(() => {
    if (fcModalCollection && fcModalCollection.length > 0) {
      const fcModal = fcModalCollection[0].parentElement!;
      if (fcModal.offsetTop + 150 > window.innerHeight)
        fcModal.style.top = `${window.innerHeight - 150}px`;
      if (fcModal.offsetLeft + 80 > window.innerWidth)
        fcModal.style.left = `${window.innerWidth - 80}px`;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handlePageResize);
    return () => window.removeEventListener('resize', handlePageResize);
  }, [handlePageResize]);

  const handleModalMovement = useCallback(e => {
    setIsDragging(true);
    e.preventDefault();
    e.stopPropagation();
    let [pos1, pos2, pos3, pos4] = [0, 0, 0, 0];
    pos3 = e.clientX;
    pos4 = e.clientY;

    document.onmouseup = () => {
      document.onmousemove = null;
      document.onmouseup = null;
    };

    document.onmousemove = e => {
      pos1 = pos3 - e.clientX;
      pos2 = pos4 - e.clientY;
      pos3 = e.clientX;
      pos4 = e.clientY;

      if (fcModalCollection && fcModalCollection.length > 0) {
        const fcModal = fcModalCollection[0].parentElement!;
        if (
          fcModal.offsetTop - pos2 + 150 < window.innerHeight &&
          fcModal.offsetTop - pos2 > -102 &&
          e.clientX + 40 < window.innerWidth &&
          e.clientX - 40 > 0
        ) {
          fcModal.style.top = `${fcModal.offsetTop - pos2}px`;
          fcModal.style.left = `${fcModal.offsetLeft - pos1 * 2}px`;
        } else {
          document.onmousemove = null;
          document.onmouseup = null;
        }
      }
    };
  }, []);

  const close = useCallback(() => {
    batch(() => {
      setSelectedType(undefined);
      dispatch(setEditingCardProps());
      dispatch(setIsInitialScrapingDone(false));
      dispatch(clearEditorData());
      dispatch(setAddFeedcardModalOpened(false));
    });
  }, [dispatch]);

  const selectCardType = useCallback(
    (item: ICard) => {
      dispatch(
        setCardProps({
          type: item.mockup.type,
          time,
          isInLibrary,
          selectedWord,
          recording,
        })
      );
    },
    [dispatch, time, isInLibrary, selectedWord, recording]
  );

  const [form] = Form.useForm();

  const {
    saveFeedCard,
    submitDisabled,
    isScraping,
    isNewResourceCardScrappedOnce,
    validationFailed,
    imageEditorValidation,
    handleRemove,
  } = useFeedCardForm(type, form, close);

  const onClickBack = useCallback(() => {
    // handle GraphicCard back functionality
    batch(() => {
      dispatch(clearImageEditorImage());

      if (selectedType === CardType.graphic && graphicFormScrollPos !== null) {
        if (
          cardProps.imageDetails?.imageTypeSelected ===
            FeedCardImageType.unsplashImage ||
          cardProps.imageDetails?.imageTypeSelected ===
            FeedCardImageType.giphyImage
        ) {
          handleRemove();

          nextTick(() => {
            const form = document.getElementsByClassName('modal-form');
            if (form && form[0]) {
              form[0].scroll({
                top: graphicFormScrollPos,
                behavior: 'auto',
              });
            }
          });
          return;
        }
      }

      setSelectedType(undefined);
      dispatch(
        setCardProps({
          time,
          recording,
          selectedWord,
          type: undefined,
          isInLibrary,
        })
      );
      dispatch(setIsInitialScrapingDone(false));
      dispatch(setFeedcardValidationFailed(false));
      dispatch(clearEditorData());
    });
  }, [
    dispatch,
    time,
    recording,
    selectedWord,
    isInLibrary,
    selectedType,
    cardProps,
    graphicFormScrollPos,
    handleRemove,
  ]);

  const handleSave = useCallback(
    async e => {
      e.preventDefault();
      e.stopPropagation();

      try {
        await saveFeedCard(editorRef);
      } catch (e) {
        console.log(e);
      }
    },
    [saveFeedCard, editorRef]
  );

  const tooltipMessage = useMemo<string | undefined>(() => {
    if (imageEditorValidation.failed) return imageEditorValidation.errorMessage;
    else return '';
  }, [imageEditorValidation]);

  useEffect(() => {
    if (!!(time && recording && !addFeedcardModalOpened)) {
      dispatch(setAddFeedcardModalOpened(true));
    }
  }, [addFeedcardModalOpened, dispatch, recording, time]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <ConfigProvider theme={feedCardModalTheme}>
      <Modal
        className={'new-feedcard-modal'}
        onCancel={close}
        style={{ top: 50 }}
        closable={true}
        width={800}
        destroyOnClose
        mask={false}
        maskClosable={false}
        open={addFeedcardModalOpened}
        getContainer={document.getElementById('root') || false}
        title={
          <div
            className={`${styles.addFeedCardTitle} ${
              isDragging ? styles.addFeedCardTitleDragging : ''
            }`}
            onMouseDown={handleModalMovement}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {selectedType
              ? `${cardProps?.id ? 'Edit' : 'Add'} ${
                  FeedcardType[selectedType][0].toUpperCase() +
                  FeedcardType[selectedType].substring(1)
                }`
              : 'Add Feed Card'}
          </div>
        }
        footer={
          selectedType ? (
            <div>
              <Tooltip title={tooltipMessage}>
                {!cardProps.id && (
                  <Button tabIndex={-1} onClick={onClickBack}>
                    Back
                  </Button>
                )}
                <Button
                  type={'primary'}
                  // className={styles.addFeedCardButton}
                  htmlType="submit"
                  onClick={handleSave}
                  disabled={
                    isScraping ||
                    submitDisabled ||
                    !isNewResourceCardScrappedOnce ||
                    !!tooltipMessage
                  }
                  loading={submitDisabled}
                >
                  {cardProps && cardProps.id ? 'Save' : 'Add'}
                </Button>
              </Tooltip>
            </div>
          ) : null
        }
      >
        {selectedType ? (
          <div className={styles.cardConfigureWrapper}>
            <CardCreateForm
              form={form}
              type={selectedType}
              setFormType={setSelectedType}
              validationFailed={validationFailed}
              saving={submitDisabled}
              editorRef={editorRef}
              setEditorRef={setEditorRef}
            />
          </div>
        ) : (
          <Row className={styles.cardTypeSelectionWrapper}>
            {CardsShowOff.map(item => (
              <FeedCardIcon
                key={item.title}
                item={item}
                onClick={selectCardType}
              />
            ))}
          </Row>
        )}
      </Modal>
    </ConfigProvider>
  );
};

export default AddFeedCardModal;
